import type { ComponentProps } from 'react';
import { cn } from '@/shared/lib';
import { fieldVariants } from './fieldStyles';

/**
 * Дата / дата и время (README «Поля форм»): нативные поля, как в прототипе (input type=date/time),
 * в стиле полей форм. Значения — строки ISO без пояса: дата `YYYY-MM-DD`, время `HH:mm`.
 */
export type DateInputProps = Omit<ComponentProps<'input'>, 'type' | 'size'>;

export function DateInput({ className, ...props }: DateInputProps) {
  return (
    <input
      type="date"
      className={cn(fieldVariants({ size: 'md' }), 'px-9', className)}
      {...props}
    />
  );
}

export function TimeInput({ className, ...props }: DateInputProps) {
  return (
    <input
      type="time"
      className={cn(fieldVariants({ size: 'md' }), 'w-104 shrink-0 px-9', className)}
      {...props}
    />
  );
}

export interface DateTimeValue {
  /** YYYY-MM-DD */
  date: string;
  /** HH:mm */
  time: string;
}

/**
 * Склейка в локальную «настенную» дату-время `YYYY-MM-DDTHH:mm` (пояс хранится отдельно, ТЗ COM-10).
 * Неполный ввод не теряется: дата без времени — `YYYY-MM-DDT`, время без даты — `THH:mm` (иначе
 * контролируемое поле сбросило бы введённую дату); полноту проверяет схема формы
 * (`zonedDateTimeToUtcIso` вернёт null). Пусто — только когда пусты обе части.
 */
export function joinDateTime(value: DateTimeValue): string {
  return value.date || value.time ? `${value.date}T${value.time}` : '';
}

export function splitDateTime(value: string): DateTimeValue {
  const [date = '', time = ''] = value.split('T');
  return { date, time: time.slice(0, 5) };
}

/**
 * Перевод «настенного» времени в часовом поясе события в момент UTC (ISO 8601 с Z, ТЗ API-01).
 * Смещение пояса вычисляется через Intl, без сторонних библиотек.
 */
export function zonedDateTimeToUtcIso(localDateTime: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(localDateTime);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  if (
    y === undefined ||
    mo === undefined ||
    d === undefined ||
    h === undefined ||
    mi === undefined
  ) {
    return null;
  }
  const asUtc = Date.UTC(y, mo - 1, d, h, mi);
  const offset = timeZoneOffsetMs(asUtc, timeZone);
  const firstGuess = asUtc - offset;
  // Повторная поправка на случай перехода на летнее/зимнее время между asUtc и firstGuess.
  const corrected = asUtc - timeZoneOffsetMs(firstGuess, timeZone);
  return new Date(corrected).toISOString();
}

function timeZoneOffsetMs(utcMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(utcMs));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? 0);
  const zoned = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return zoned - Math.floor(utcMs / 1000) * 1000;
}

/** IANA-пояс, который понимает Intl; иначе — пояс браузера (подпись не должна ронять экран). */
function knownTimeZone(timeZone: string): string | undefined {
  try {
    return Intl.DateTimeFormat('en-US', { timeZone }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

/**
 * Момент UTC → «настенные» дата и время в поясе события `YYYY-MM-DDTHH:mm` — значение поля даты
 * и времени в формах (обратно — `zonedDateTimeToUtcIso`). Нет момента — пустая строка.
 */
export function utcToZonedDateTime(value: string | null | undefined, timeZone: string): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: knownTimeZone(timeZone),
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}T${part('hour')}:${part('minute')}`;
}

/** Подпись часового пояса: «Europe/Moscow · UTC+03:00». */
export function formatTimeZoneLabel(timeZone: string, at: Date = new Date()): string {
  const offsetMinutes = Math.round(timeZoneOffsetMs(at.getTime(), timeZone) / 60_000);
  const sign = offsetMinutes >= 0 ? '+' : '−';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return `${timeZone} · UTC${sign}${hours}:${minutes}`;
}
